import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { applyPropertyTheme, clearPropertyTheme, derivePropertyTheme } from '@/ds'
import { API_LOCALE, isLanguage } from '@/i18n'
import { setStayContext } from '@/lib/monitoring'
import { readQuery } from '@/lib/query-params'
import { isServiceError } from '@/services/errors'
import type { ServiceErrorCode } from '@/services/errors'
import { reservaService } from '@/services/reserva'
import type { Stay } from '@/services/reserva'

type Status = { kind: 'loading' } | { kind: 'ready'; stay: Stay } | { kind: 'error'; code: ServiceErrorCode; guestCode: string }

interface StayContextValue {
  status: Status
  guestCode: string
  reload: () => void
  /** Sustituye la reserva por la que devuelve un cambio ya aplicado (bloque 5), sin volver a cargar la pantalla */
  replaceStay: (stay: Stay) => void
}

const StayContext = createContext<StayContextValue | null>(null)
const CACHE_KEY = 'v2.stay'

function readCache(guestCode: string): Stay | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { guestCode: string; stay: Stay }
    return parsed.guestCode === guestCode ? parsed.stay : null
  } catch {
    return null
  }
}

function writeCache(guestCode: string, stay: Stay) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ guestCode, stay }))
  } catch {
    /* sin almacenamiento: se ignora (D18) */
  }
}

/** Carga la reserva desde el enlace mágico (?guestcode=), aplica el tema de la propiedad y cachea para el modo sin conexión. */
export function StayProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const { guestCode } = readQuery()
  const [status, setStatus] = useState<Status>(() => (guestCode ? { kind: 'loading' } : { kind: 'error', code: 'link_invalid', guestCode: '' }))
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!guestCode) return
    const controller = new AbortController()
    // El idioma del dispositivo es la única fuente de verdad; `langid` de enlaces antiguos se ignora.
    const lang = isLanguage(i18n.language) ? i18n.language : 'en'
    reservaService
      .getStay(guestCode, API_LOCALE[lang], controller.signal)
      .then((stay) => {
        writeCache(guestCode, stay)
        setStatus({ kind: 'ready', stay })
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        const code: ServiceErrorCode = isServiceError(e) ? e.code : 'server'
        const cached = code === 'offline' || code === 'server' ? readCache(guestCode) : null
        if (cached) setStatus({ kind: 'ready', stay: cached })
        else setStatus({ kind: 'error', code, guestCode })
      })
    return () => controller.abort()
  }, [guestCode, i18n.language, attempt])

  useEffect(() => {
    if (status.kind !== 'ready') return
    setStayContext(status.stay.id, status.stay.guestCode)
    applyPropertyTheme(derivePropertyTheme(status.stay.property.primaryColor))
    return () => clearPropertyTheme()
  }, [status])

  const reload = useCallback(() => {
    setStatus({ kind: 'loading' })
    setAttempt((a) => a + 1)
  }, [])
  const replaceStay = useCallback(
    (stay: Stay) => {
      writeCache(guestCode, stay)
      setStatus({ kind: 'ready', stay })
    },
    [guestCode],
  )
  const value = useMemo(() => ({ status, guestCode, reload, replaceStay }), [status, guestCode, reload, replaceStay])
  return <StayContext.Provider value={value}>{children}</StayContext.Provider>
}

export function useStay(): StayContextValue {
  const ctx = useContext(StayContext)
  if (!ctx) throw new Error('useStay debe usarse dentro de StayProvider')
  return ctx
}

/** Solo cuando la reserva está cargada (dentro de las pestañas). */
export function useLoadedStay(): Stay {
  const { status } = useStay()
  if (status.kind !== 'ready') throw new Error('La reserva aún no está cargada')
  return status.stay
}
